import { mapValues } from 'es-toolkit';
import React, { useMemo } from 'react';
import { Layer, Line, Stage } from 'react-konva';

import { Point2D } from '@paragon/contract-library';
import { distanceEquals, Point2D as Point2DGeometry } from '@paragon/geometry-library';
import { InlineNotification } from '@paragon/notification-tools';
import { notNullish } from '@paragon/utilities';

import { BundlePositioningViewTruss } from './BundlePositioningViewTruss';
import { BUNDLE_MAX_THICKNESS_MARKER } from './constants';
import { InstanceInfo, InstancePositions } from './types';

interface BundlePositioningViewEventHandlers {
  readonly onClick: (productionComponentInstanceGuid: string) => void;
  readonly setInstancePositions?: (updatedPositions: InstancePositions) => void;
}

interface BundlePositioningViewProps {
  readonly height: number;
  readonly width: number;
  readonly instanceInfo: InstanceInfo;
  readonly instancePositions: InstancePositions;
  readonly distanceFormatter: (distance: number) => string;
  readonly eventHandlers: BundlePositioningViewEventHandlers;
  readonly rightAndBottomPaddingAsAFractionOfOverallHeightOrWidth?: number;
  readonly selectedInstanceGuid?: string;
  readonly showFourFootLine?: boolean;
  readonly disabled?: boolean;
}

export function BundlePositioningView({
  width,
  height,
  instanceInfo,
  instancePositions,
  distanceFormatter,
  rightAndBottomPaddingAsAFractionOfOverallHeightOrWidth,
  selectedInstanceGuid,
  eventHandlers,
  showFourFootLine,
  disabled,
}: BundlePositioningViewProps) {
  const { xScale, yScale } = useMemo(() => {
    const maxX = Math.max(
      ...Object.entries(instancePositions).map(
        ([productionComponentInstanceGuid, position]) =>
          position.x + (instanceInfo[productionComponentInstanceGuid]?.width ?? 0),
      ),
    );
    const maxY = Math.max(
      ...Object.entries(instancePositions).map(
        ([productionComponentInstanceGuid, position]) =>
          position.y + (instanceInfo[productionComponentInstanceGuid]?.thickness ?? 0),
      ),
    );
    // Leave room on the right side equal to 10% of the widest truss
    const rightEdgeBundleBuilderPadding = maxX * (rightAndBottomPaddingAsAFractionOfOverallHeightOrWidth ?? 0);
    const xScale = maxX === 0 ? 1 : width / (maxX + rightEdgeBundleBuilderPadding);
    // Leave room at the bottom to move the thickest truss and still have it visible while dragging
    const bottomEdgeBundleBuilderPadding = maxY * (rightAndBottomPaddingAsAFractionOfOverallHeightOrWidth ?? 0);
    const bundleBuilderHeight = Math.max(BUNDLE_MAX_THICKNESS_MARKER, maxY) + bottomEdgeBundleBuilderPadding;
    const yScale = maxY === 0 ? 1 : height / bundleBuilderHeight;
    return { xScale, yScale };
  }, [height, instanceInfo, width, instancePositions, rightAndBottomPaddingAsAFractionOfOverallHeightOrWidth]);

  function updatePositions(productionComponentInstanceGuid: string, newPosition: Point2D) {
    const { setInstancePositions } = eventHandlers;
    if (!setInstancePositions) return;
    // Handle situations where the truss was dragged to a negative x and/or y by adjusting the origin
    // based on that truss's new position
    if (newPosition.x < 0 || newPosition.y < 0) {
      const otherInstancePositions = { ...instancePositions };
      delete otherInstancePositions[productionComponentInstanceGuid];
      let updatedPositions: InstancePositions | null = null;
      const xShift = Math.abs(newPosition.x / xScale);
      const yShift = Math.abs(newPosition.y / yScale);
      updatedPositions = mapValues(otherInstancePositions, (position) => ({
        x: newPosition.x < 0 ? position.x + xShift : position.x,
        y: newPosition.y < 0 ? position.y + yShift : position.y,
      }));
      updatedPositions[productionComponentInstanceGuid] = {
        x: newPosition.x < 0 ? 0 : instancePositions[productionComponentInstanceGuid].x,
        y: newPosition.y < 0 ? 0 : instancePositions[productionComponentInstanceGuid].y,
      };
      setInstancePositions(updatedPositions);
      return;
    }

    const updatedInstancePosition = {
      x: newPosition.x / xScale,
      y: newPosition.y / yScale,
    };

    // The user may have made an update that means there is extra space on the left or top that could be collapsed, for
    // example, after moving a truss into negative territory like in the case above. When this extra space exists, we
    // want to collapse it for uniformity.
    const updatedInstancePositionsDraft = {
      ...instancePositions,
      [productionComponentInstanceGuid]: updatedInstancePosition,
    };
    const minX = Math.min(...Object.values(updatedInstancePositionsDraft).map((point) => point.x));
    const minY = Math.min(...Object.values(updatedInstancePositionsDraft).map((point) => point.y));
    if (!distanceEquals(minX, 0) || !distanceEquals(minY, 0)) {
      const updatedPositions = mapValues(updatedInstancePositionsDraft, (position) => ({
        x: distanceEquals(minX, 0) ? position.x : position.x - minX,
        y: distanceEquals(minY, 0) ? position.y : position.y - minY,
      }));
      setInstancePositions(updatedPositions);
      return;
    }

    // The basic case, where a truss has been dragged to a new position neither of whose points are negative and not
    // causing extra space on the top or left.
    setInstancePositions({
      [productionComponentInstanceGuid]: updatedInstancePosition,
    });
  }

  const fourFootMarker = BUNDLE_MAX_THICKNESS_MARKER * yScale;

  return (
    <>
      {Object.keys(instanceInfo).length !== Object.keys(instancePositions).length && (
        <InlineNotification type="error">Not all components are displayed</InlineNotification>
      )}
      <Stage
        width={width}
        height={height}
        style={{ position: 'relative', border: '1px solid black', pointerEvents: disabled ? 'none' : undefined }}
      >
        <Layer>
          {Object.entries(instancePositions)
            .map(([productionComponentInstanceGuid, position]) =>
              productionComponentInstanceGuid in instanceInfo ? (
                <BundlePositioningViewTruss
                  key={productionComponentInstanceGuid}
                  xScale={xScale}
                  yScale={yScale}
                  instanceInfo={instanceInfo[productionComponentInstanceGuid]}
                  position={position}
                  snappingPoints={
                    eventHandlers.setInstancePositions
                      ? getSnappingPositions(
                          productionComponentInstanceGuid,
                          instancePositions,
                          instanceInfo,
                          xScale,
                          yScale,
                        )
                      : undefined
                  }
                  onClick={() => eventHandlers.onClick(productionComponentInstanceGuid)}
                  updatePosition={
                    eventHandlers.setInstancePositions
                      ? (newPosition) => void updatePositions(productionComponentInstanceGuid, newPosition)
                      : undefined
                  }
                  disabled={disabled}
                  selected={selectedInstanceGuid ? productionComponentInstanceGuid === selectedInstanceGuid : undefined}
                  distanceFormatter={distanceFormatter}
                />
              ) : null,
            )
            .filter(notNullish)}
          {showFourFootLine && (
            <Line points={[0, fourFootMarker, 10000, fourFootMarker]} dashEnabled dash={[2]} stroke="blue" />
          )}
        </Layer>
      </Stage>
    </>
  );
}

function getSnappingPositions(
  selfGuid: string,
  instancePositions: InstancePositions,
  instanceInfo: InstanceInfo,
  xScale: number,
  yScale: number,
) {
  const snappingPositions: Point2DGeometry[] = [];
  for (const [productionComponentInstanceGuid, position] of Object.entries(instancePositions)) {
    if (productionComponentInstanceGuid === selfGuid) continue;

    // Get snapping positions at all corners of the truss
    snappingPositions.push(Point2DGeometry.fromObject({ x: position.x * xScale, y: position.y * yScale }));
    const { thickness, width } = instanceInfo[productionComponentInstanceGuid];
    snappingPositions.push(Point2DGeometry.fromObject({ x: (position.x + width) * xScale, y: position.y * yScale }));
    snappingPositions.push(
      Point2DGeometry.fromObject({ x: position.x * xScale, y: (position.y + thickness) * yScale }),
    );
    snappingPositions.push(
      Point2DGeometry.fromObject({ x: (position.x + width) * xScale, y: (position.y + thickness) * yScale }),
    );
  }
  return snappingPositions;
}
